import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Gavel,
  ShieldAlert,
  FileText,
  Image as ImageIcon,
  Music,
  Video,
} from "lucide-react";
import { toast } from "sonner";

function FileIcon({ mime, size = 14 }) {
  if (!mime) return <FileText size={size} />;
  if (mime.startsWith("image/")) return <ImageIcon size={size} />;
  if (mime.startsWith("audio/")) return <Music size={size} />;
  if (mime.startsWith("video/")) return <Video size={size} />;
  return <FileText size={size} />;
}

function EvidenceMedia({ file }) {
  const { mime, data_url, name } = file;
  if (mime?.startsWith("image/")) {
    return (
      <a href={data_url} target="_blank" rel="noopener noreferrer" title={name}
         className="block border border-[#EAE3DE] rounded-lg overflow-hidden hover:border-[#B31E1E] transition-all">
        <img src={data_url} alt={name} className="h-28 w-28 object-cover" />
      </a>
    );
  }
  if (mime?.startsWith("audio/")) {
    return (
      <div className="flex items-center gap-2 border border-[#EAE3DE] rounded-lg px-3 py-2 bg-white">
        <Music size={14} className="text-[#B31E1E]" />
        <audio src={data_url} controls className="h-8 min-w-[200px]" />
      </div>
    );
  }
  if (mime?.startsWith("video/")) {
    return (
      <div className="border border-[#EAE3DE] rounded-lg overflow-hidden bg-black">
        <video src={data_url} controls className="max-h-48 w-auto" />
      </div>
    );
  }
  return (
    <a href={data_url} target="_blank" rel="noopener noreferrer" download={name}
       className="flex items-center gap-2 border border-[#EAE3DE] rounded-lg px-3 py-2 text-xs bg-white hover:border-[#B31E1E]">
      <FileText size={14} className="text-[#B31E1E]" /> {name}
    </a>
  );
}

function VerdictForm({ review, onResolved }) {
  const [status, setStatus] = useState("resolved");
  const [action, setAction] = useState("kept");
  const [verdict, setVerdict] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (verdict.trim().length < 10) {
      toast.error("Write at least a short verdict explaining the decision (10+ chars)");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/reviews/${review.id}/mediation/verdict`, {
        status,
        verdict,
        action: status === "dismissed" ? "" : action,
      });
      toast.success("Verdict recorded");
      onResolved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-[#EAE3DE] pt-4 mt-4 space-y-3" data-testid={`mediation-verdict-form-${review.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C4E48] mb-1">
            Decision
          </label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-lg" data-testid={`mediation-status-${review.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="resolved">Resolved with verdict</SelectItem>
              <SelectItem value="dismissed">Dismissed (no action)</SelectItem>
              <SelectItem value="in_review">Keep in review</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {status === "resolved" && (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C4E48] mb-1">
              Action on the review
            </label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="rounded-lg" data-testid={`mediation-action-${review.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="kept">Keep review as-is</SelectItem>
                <SelectItem value="hidden">Hide review from public</SelectItem>
                <SelectItem value="edited">Ask author to edit</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C4E48] mb-1">
          Verdict text (visible to both parties)
        </label>
        <Textarea
          rows={4}
          value={verdict}
          onChange={(e) => setVerdict(e.target.value)}
          className="rounded-lg"
          placeholder="Summarise your reasoning based on the evidence submitted by both sides."
          data-testid={`mediation-verdict-${review.id}`}
        />
      </div>
      <Button
        onClick={submit}
        disabled={saving}
        className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white"
        data-testid={`mediation-verdict-submit-${review.id}`}
      >
        {saving ? "Recording..." : "Record verdict"}
      </Button>
    </div>
  );
}

export default function AdminMediationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/mediations");
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-[#8B7B73] mt-4">Loading mediations...</p>;
  if (rows.length === 0) {
    return (
      <div className="mt-6 text-center py-14 border border-dashed border-[#EAE3DE] rounded-xl" data-testid="admin-mediations-empty">
        <Gavel className="mx-auto text-[#B31E1E] mb-3" />
        <p className="text-[#5C4E48] font-medium">No open mediation requests</p>
        <p className="text-[#8B7B73] text-sm mt-1">
          Disputes escalated for admin review will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4" data-testid="admin-mediations-list">
      {rows.map((r) => {
        const disputes = r.disputes || [];
        const med = r.mediation || {};
        const statusPill =
          med.status === "in_review"
            ? "bg-[#F9E4E1] text-[#B31E1E] border-[#F0C9C4]"
            : "bg-[#FEF3E7] text-[#8A4B00] border-[#F5D9A5]";
        return (
          <Card
            key={r.id}
            className="rounded-xl border border-[#EAE3DE] bg-white p-5"
            data-testid={`admin-mediation-${r.id}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <ShieldAlert size={14} className="text-[#B31E1E]" />
                  <p className="font-semibold text-[#1B1614]">
                    Review · {r.rating}★
                  </p>
                  <Badge variant="outline" className={`rounded-full text-[10px] font-mono border ${statusPill}`}>
                    {med.status}
                  </Badge>
                </div>
                <p className="text-xs font-mono text-[#8B7B73] mt-1">
                  {r.type} · {r.author_name || r.author_id} → {r.target_name || r.target_id} · {new Date(r.created_at).toLocaleString()}
                </p>
              </div>
              <div className="text-right text-xs text-[#8B7B73]">
                <div>Opened: {med.opened_at ? new Date(med.opened_at).toLocaleDateString() : "—"}</div>
                {med.escalated_at && (
                  <div>Escalated: {new Date(med.escalated_at).toLocaleDateString()}</div>
                )}
              </div>
            </div>

            <div className="mt-3 rounded-lg bg-[#FAF5F2] border border-[#EAE3DE] p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#5C4E48] mb-1.5">
                Review comment
              </p>
              <p className="text-sm text-[#1B1614] whitespace-pre-wrap">
                {r.comment || <em className="text-[#8B7B73]">(no comment)</em>}
              </p>
            </div>

            {med.escalation_reason && (
              <div className="mt-3 rounded-lg bg-[#F9E4E1]/40 border border-[#F0C9C4] p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#B31E1E] mb-1.5">
                  Escalation reason
                </p>
                <p className="text-sm text-[#1B1614] whitespace-pre-wrap">
                  {med.escalation_reason}
                </p>
              </div>
            )}

            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#5C4E48] mb-2">
                Evidence submitted ({disputes.length})
              </p>
              <div className="space-y-3">
                {disputes.map((d) => (
                  <div key={d.id} className="border border-[#EAE3DE] rounded-lg p-3 bg-white">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs font-mono text-[#3E2A26]">
                        {d.author_name} · {d.author_role === "business" ? "Business" : "Customer"}
                        {d.side === "target" ? " (subject)" : " (author)"}
                      </span>
                      <span className="text-[10px] font-mono text-[#8B7B73]">
                        {new Date(d.created_at).toLocaleString()}
                      </span>
                    </div>
                    {d.comment && (
                      <p className="mt-1.5 text-sm text-[#1B1614] whitespace-pre-wrap">
                        {d.comment}
                      </p>
                    )}
                    {d.files?.length > 0 && (
                      <ul className="mt-2 flex flex-wrap gap-2">
                        {d.files.map((f, i) => (
                          <li key={`${d.id}-${i}`} data-testid={`admin-mediation-file-${d.id}-${i}`}>
                            <EvidenceMedia file={f} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <VerdictForm review={r} onResolved={load} />
          </Card>
        );
      })}
    </div>
  );
}
